import { useEffect, useRef, useState } from "react";
import type { LiveEnvelope } from "../types";

type LiveState = "connecting" | "live" | "polling" | "offline";
type LiveTopic = LiveEnvelope["topic"];

export interface LiveUpdateOptions {
  enabled: boolean;
  url?: string;
  pollIntervalMs?: number;
  onEvent(event: LiveEnvelope): void;
  onPoll(): Promise<void> | void;
  eventSourceFactory?: (url: string) => EventSource;
}

const TOPICS: LiveTopic[] = ["board", "jobs", "workers", "settings", "system"];
const DEFAULT_URL = import.meta.env.VITE_LIVE_UPDATES_URL ?? "/api/events/stream";
const DEFAULT_POLL_MS = Number(import.meta.env.VITE_POLL_INTERVAL_MS ?? 15000);

export function useLiveUpdates({
  enabled,
  url = DEFAULT_URL,
  pollIntervalMs = DEFAULT_POLL_MS,
  onEvent,
  onPoll,
  eventSourceFactory = (streamUrl) => new EventSource(streamUrl, { withCredentials: true }),
}: LiveUpdateOptions): LiveState {
  const [state, setState] = useState<LiveState>(enabled ? "connecting" : "offline");
  const callbacks = useRef({ onEvent, onPoll });
  callbacks.current = { onEvent, onPoll };

  useEffect(() => {
    if (!enabled) {
      setState("offline");
      return;
    }

    const source = eventSourceFactory(url);
    let pollTimer: number | null = null;

    const startPolling = () => {
      if (pollTimer !== null) return;
      setState("polling");
      void callbacks.current.onPoll();
      pollTimer = window.setInterval(() => {
        void callbacks.current.onPoll();
      }, pollIntervalMs);
    };

    const stopPolling = () => {
      if (pollTimer === null) return;
      window.clearInterval(pollTimer);
      pollTimer = null;
    };

    source.onopen = () => {
      stopPolling();
      setState("live");
    };
    source.onerror = startPolling;

    const listeners = TOPICS.map((topic) => {
      const listener = (rawEvent: Event) => {
        const event = rawEvent as MessageEvent<string>;
        try {
          callbacks.current.onEvent({
            id: event.lastEventId,
            topic,
            occurredAt: null,
            payload: JSON.parse(event.data) as unknown,
          });
        } catch {
          void callbacks.current.onPoll();
        }
      };
      source.addEventListener(topic, listener);
      return { topic, listener };
    });

    return () => {
      listeners.forEach(({ topic, listener }) => source.removeEventListener(topic, listener));
      source.close();
      stopPolling();
    };
  }, [enabled, eventSourceFactory, pollIntervalMs, url]);

  return state;
}
